import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Loader2,
  Mail,
  Send,
  Shield,
  X,
} from 'lucide-react'
import CareerGrowthLogo from '../components/CareerGrowthLogo'
import CandidateFooter from '../components/CandidateFooter'
import { api, APIError } from '../api/client'
import { LEGAL_VERSIONS } from '../config/legalVersions'

/**
 * Public candidate-facing screening page at jobskills.work/screen/:token.
 *
 * Flow:
 *   1. Identity step — confirm the email recruiter pre-filled (or change it),
 *      receive a 6-digit OTP, type it in.
 *   2. Editor — answer questions in any order. Auto-save per question.
 *      Two CTAs: implicit "save draft" (on blur / timer) and explicit
 *      "Submit final answers" (locks the screening).
 *
 * No auth — token-based access. is_email_verified persists across sessions
 * via candidate.verified_at on the backend, so returning to the same link
 * after closing the browser doesn't require a second OTP.
 */

const RESPONSE_LIMIT = 3000
const AUTOSAVE_DEBOUNCE_MS = 1500

export default function ScreeningScreen() {
  const { token } = useParams()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const reload = useCallback(async () => {
    try {
      const fresh = await api.get(`/api/b2b/public/screenings/${token}`)
      setData(fresh)
      return fresh
    } catch (err) {
      if (err instanceof APIError) {
        if (err.status === 404) setError('This screening link is not valid.')
        else if (err.status === 410) setError('This screening link has expired.')
        else setError(err.data?.detail || err.message || 'Could not load screening.')
      } else {
        setError('Network error. Please refresh.')
      }
      return null
    }
  }, [token])

  useEffect(() => {
    let cancelled = false
    reload().finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [reload])

  if (loading) {
    return (
      <Shell>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      </Shell>
    )
  }

  if (error && !data) {
    return (
      <Shell>
        <div className="text-center max-w-md">
          <div className="w-14 h-14 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mx-auto">
            <AlertCircle className="w-7 h-7" />
          </div>
          <h1 className="mt-4 text-2xl font-bold text-[#1E293B]">This link is no longer available</h1>
          <p className="mt-2 text-sm text-gray-500">{error}</p>
          <a
            href="https://jobskills.work"
            className="mt-6 inline-block bg-[#F47920] hover:bg-[#E06810] text-white font-bold py-3 px-6 rounded-2xl transition"
          >
            Visit jobskills.work
          </a>
        </div>
      </Shell>
    )
  }

  if (!data) return null

  // Terminal states — show a thank-you / status screen.
  if (data.status === 'completed') {
    return (
      <Shell>
        <Done variant="completed" data={data} />
      </Shell>
    )
  }
  if (data.status === 'declined') {
    return (
      <Shell>
        <Done variant="declined" data={data} />
      </Shell>
    )
  }
  if (data.status === 'expired') {
    return (
      <Shell>
        <Done variant="expired" data={data} />
      </Shell>
    )
  }

  // Identity step — needs verification before showing editor.
  if (!data.is_email_verified) {
    return (
      <Shell>
        <IdentityStep token={token} data={data} onVerified={reload} />
      </Shell>
    )
  }

  // Consent step (EU AI Act 2026) — explicit opt-in to AI processing
  // BEFORE any answer can be submitted. The backend rejects /answers and
  // /submit with 403 until candidate_consent_at is set.
  if (!data.is_consented) {
    return (
      <Shell>
        <ConsentStep token={token} data={data} onConsented={reload} />
      </Shell>
    )
  }

  // Editor — verified + consented, can answer.
  return (
    <Shell wide>
      <Editor token={token} data={data} onChange={reload} />
    </Shell>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Shell
// ─────────────────────────────────────────────────────────────────────────────

function Shell({ children, wide = false }) {
  return (
    <div className="min-h-screen bg-[#F6F7FB] flex flex-col items-center px-4 py-8">
      <div className="mb-6">
        <CareerGrowthLogo />
      </div>
      <div className={`flex-1 w-full flex items-start justify-center ${wide ? '' : ''}`}>
        {children}
      </div>
      <CandidateFooter />
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Identity step — confirm email + OTP
// ─────────────────────────────────────────────────────────────────────────────

function IdentityStep({ token, data, onVerified }) {
  const [email, setEmail] = useState(data.candidate_email || '')
  const [phase, setPhase] = useState('email')   // 'email' | 'code'
  const [code, setCode] = useState('')
  const [maskedTo, setMaskedTo] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [cooldown, setCooldown] = useState(0)

  // Cooldown timer for "Resend code".
  useEffect(() => {
    if (cooldown <= 0) return
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000)
    return () => clearTimeout(id)
  }, [cooldown])

  const sendCode = async () => {
    setError('')
    setBusy(true)
    try {
      const resp = await api.post(`/api/b2b/public/screenings/${token}/email/send-code`, { email })
      setMaskedTo(resp.sent_to_masked || '')
      setCooldown(resp.cooldown_seconds || 60)
      setPhase('code')
    } catch (err) {
      const detail = err instanceof APIError ? (err.data?.detail || err.message) : err.message
      setError(typeof detail === 'string' ? detail : 'Could not send code.')
    } finally {
      setBusy(false)
    }
  }

  const verify = async () => {
    setError('')
    setBusy(true)
    try {
      await api.post(`/api/b2b/public/screenings/${token}/email/verify`, { code })
      await onVerified()
    } catch (err) {
      const detail = err instanceof APIError ? (err.data?.detail || err.message) : err.message
      setError(typeof detail === 'string' ? detail : 'Could not verify code.')
      setBusy(false)
    }
  }

  const minutesLabel = useMemo(() => {
    const total = data.questions?.length || 0
    if (total === 0) return ''
    const lo = Math.max(5, Math.round(total * 1.5))
    const hi = Math.round(total * 2)
    return `~${lo}–${hi} minutes`
  }, [data.questions])

  return (
    <div className="w-full max-w-md">
      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-7">
        <p className="text-xs font-bold uppercase tracking-wider text-[#F47920]">
          Hi {data.candidate_name.split(/\s+/)[0]}
        </p>
        <h1 className="mt-1 text-[22px] font-bold text-[#1E293B] leading-tight">
          Screening for {data.job_title}
        </h1>
        <p className="mt-2 text-sm text-gray-500">
          <strong>{data.workspace_name}</strong> invited you to answer a few questions.{' '}
          {minutesLabel && <>This usually takes {minutesLabel}.</>}
        </p>

        {phase === 'email' && (
          <>
            <div className="mt-6 rounded-2xl bg-gray-50 px-4 py-3 flex items-start gap-2">
              <Shield className="w-4 h-4 mt-0.5 text-gray-500 flex-shrink-0" />
              <p className="text-xs text-gray-500 leading-relaxed">
                We'll send a 6-digit code to confirm this is you. Your answers go directly to <strong>{data.workspace_name}</strong>.
              </p>
            </div>

            <label className="mt-5 block">
              <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Your email</span>
              <div className="mt-2 flex items-center gap-2 rounded-2xl border border-gray-200 bg-white px-4 py-3 focus-within:border-[#F47920] transition">
                <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <input
                  type="email"
                  className="flex-1 outline-none text-[15px]"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={busy}
                />
              </div>
            </label>

            {error && (
              <div className="mt-4 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
                {error}
              </div>
            )}

            <button
              type="button"
              onClick={sendCode}
              disabled={busy || !email.trim()}
              className="mt-5 w-full bg-[#F47920] hover:bg-[#E06810] text-white font-bold py-3 px-6 rounded-2xl transition disabled:opacity-50"
            >
              {busy ? 'Sending…' : 'Get code'}
            </button>
          </>
        )}

        {phase === 'code' && (
          <>
            <p className="mt-5 text-sm text-gray-500">
              We sent a 6-digit code to <strong>{maskedTo}</strong>. It expires in 30 minutes.
            </p>

            <label className="mt-4 block">
              <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Verification code</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                className="mt-2 w-full text-center text-2xl font-bold tracking-[0.5em] outline-none rounded-2xl border border-gray-200 bg-white px-4 py-3 focus:border-[#F47920] transition"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                disabled={busy}
              />
            </label>

            {error && (
              <div className="mt-3 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
                {error}
              </div>
            )}

            <button
              type="button"
              onClick={verify}
              disabled={busy || code.length !== 6}
              className="mt-4 w-full bg-[#F47920] hover:bg-[#E06810] text-white font-bold py-3 px-6 rounded-2xl transition disabled:opacity-50"
            >
              {busy ? 'Verifying…' : 'Verify and continue'}
            </button>

            <div className="mt-4 flex items-center justify-between text-xs text-gray-500">
              <button
                type="button"
                onClick={() => { setPhase('email'); setCode(''); setError('') }}
                className="hover:text-gray-800"
                disabled={busy}
              >
                ← Use a different email
              </button>
              <button
                type="button"
                onClick={sendCode}
                disabled={busy || cooldown > 0}
                className="hover:text-gray-800 disabled:opacity-50"
              >
                {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Consent step — EU AI Act 2026 mandatory disclosure + opt-in
// Shown ONCE per token after email verification. Backend rejects answer
// submissions until candidate_consent_at is set.
// ─────────────────────────────────────────────────────────────────────────────

function ConsentStep({ token, data, onConsented }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const consent = async () => {
    setError('')
    setBusy(true)
    try {
      await api.post(`/api/b2b/public/screenings/${token}/consent`, {
        version: data.consent_required_version || LEGAL_VERSIONS.AI_PROCESSING,
      })
      await onConsented()
    } catch (err) {
      const detail = err instanceof APIError ? (err.data?.detail || err.message) : err.message
      setError(typeof detail === 'string' ? detail : 'Could not record your consent.')
      setBusy(false)
    }
  }

  const retentionMonths = data?.retention_months || 12
  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 px-7 py-8">
        <div className="flex items-center gap-2.5">
          <Shield className="w-5 h-5 text-[#F47920]" />
          <h2 className="text-xl font-bold text-gray-900">Before you start</h2>
        </div>
        <p className="mt-3 text-sm text-gray-600 leading-relaxed">
          Your written answers will be analyzed by AI to generate a structured
          report for <strong>{data.workspace_name}</strong>. A human recruiter
          will also review your answers and any AI-generated scores.
        </p>

        <div className="mt-4 rounded-xl bg-gray-50 px-4 py-3 text-[12px] text-gray-700 leading-relaxed">
          <strong className="text-gray-900">Who processes your responses:</strong>
          <ul className="mt-1 space-y-0.5 list-disc list-inside">
            <li>Jobskills (the platform) — stores and presents responses to the recruiter.</li>
            <li>DeepInfra, Inc. (USA) — runs the Llama 3.3 70B AI model.</li>
            <li>{data.workspace_name} — the recruiter who invited you.</li>
          </ul>
          <p className="mt-2">
            Your responses are retained for ~{retentionMonths} months, then
            automatically deleted. Read the full{' '}
            <Link to="/screen/privacy" target="_blank" className="text-[#F47920] underline">
              Candidate Privacy Notice
            </Link>.
          </p>
        </div>

        <ul className="mt-5 space-y-2 text-sm text-gray-700">
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-[#F47920] mt-0.5 flex-shrink-0" />
            <span>You may stop or decline this screening at any time.</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-[#F47920] mt-0.5 flex-shrink-0" />
            <span>
              You can request access to or deletion of your data —{' '}
              <a
                href="mailto:privacy@jobskills.work"
                className="text-[#F47920] hover:underline"
              >
                privacy@jobskills.work
              </a>.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-[#F47920] mt-0.5 flex-shrink-0" />
            <span>
              The final hiring decision is made by a human, not by the AI.
            </span>
          </li>
        </ul>

        <p className="mt-5 text-[11px] text-gray-400">
          Disclosure version {data.consent_required_version || LEGAL_VERSIONS.AI_PROCESSING} —
          required by the EU AI Act 2026 and GDPR.
        </p>

        {error && (
          <div className="mt-4 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
            {error}
          </div>
        )}

        <button
          type="button"
          className="mt-6 w-full bg-[#F47920] hover:bg-[#E06A10] disabled:opacity-50 transition text-white font-bold py-3 rounded-2xl"
          onClick={consent}
          disabled={busy}
        >
          {busy ? (
            <span className="inline-flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Recording…
            </span>
          ) : (
            'I consent — continue to questions'
          )}
        </button>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Editor — split-pane on desktop, accordion on mobile
// ─────────────────────────────────────────────────────────────────────────────

function Editor({ token, data, onChange }) {
  const questions = useMemo(
    () => [...(data.questions || [])].sort((a, b) => a.position - b.position),
    [data.questions]
  )
  const [drafts, setDrafts] = useState(() => {
    const seed = {}
    for (const q of questions) seed[q.position] = q.draft_text || ''
    return seed
  })
  const [active, setActive] = useState(questions[0]?.position ?? 0)
  const [savingMap, setSavingMap] = useState({})           // position → 'saving' | 'saved' | undefined
  const [savedAt, setSavedAt] = useState(null)
  const [submitError, setSubmitError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false)
  const [showDecline, setShowDecline] = useState(false)
  const debounceRef = useRef({})

  // Resync drafts when polling refreshes data.
  useEffect(() => {
    if (!data?.questions) return
    setDrafts((prev) => {
      const next = { ...prev }
      for (const q of data.questions) {
        if (next[q.position] === undefined) next[q.position] = q.draft_text || ''
      }
      return next
    })
  }, [data])

  const answeredCount = useMemo(
    () => questions.filter((q) => (drafts[q.position] || '').trim().length > 0).length,
    [questions, drafts]
  )

  const saveDraft = useCallback(async (position, text) => {
    setSavingMap((m) => ({ ...m, [position]: 'saving' }))
    try {
      await api.put(`/api/b2b/public/screenings/${token}/answers/${position}`, { text })
      setSavingMap((m) => ({ ...m, [position]: 'saved' }))
      setSavedAt(new Date())
    } catch {
      setSavingMap((m) => ({ ...m, [position]: undefined }))
    }
  }, [token])

  const onTextChange = (position, text) => {
    if (text.length > RESPONSE_LIMIT) text = text.slice(0, RESPONSE_LIMIT)
    setDrafts((prev) => ({ ...prev, [position]: text }))
    // Debounce save per-question.
    clearTimeout(debounceRef.current[position])
    debounceRef.current[position] = setTimeout(() => {
      saveDraft(position, text)
    }, AUTOSAVE_DEBOUNCE_MS)
  }

  const onTextBlur = (position) => {
    clearTimeout(debounceRef.current[position])
    saveDraft(position, drafts[position] || '')
  }

  const finalSubmit = async () => {
    setSubmitError('')
    setSubmitting(true)
    try {
      await api.post(`/api/b2b/public/screenings/${token}/submit`, {})
      await onChange()
    } catch (err) {
      const detail = err instanceof APIError ? (err.data?.detail || err.message) : err.message
      setSubmitError(typeof detail === 'string' ? detail : 'Could not submit. Please try again.')
      setSubmitting(false)
      setShowSubmitConfirm(false)
    }
  }

  const declineSubmit = async (reason) => {
    setSubmitting(true)
    try {
      await api.post(`/api/b2b/public/screenings/${token}/decline`, { reason: reason || null })
      await onChange()
    } catch {
      setSubmitting(false)
    }
  }

  const total = questions.length

  return (
    <div className="w-full max-w-5xl">
      {/* Header */}
      <div className="mb-4 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-gray-400">Screening for</p>
          <h1 className="mt-0.5 text-xl md:text-2xl font-bold text-[#1E293B]">{data.job_title}</h1>
          <p className="text-xs text-gray-500">{data.workspace_name}</p>
        </div>
        <div className="flex items-center gap-3">
          <SavedIndicator state={savingMap[active]} at={savedAt} />
          <Progress answered={answeredCount} total={total} />
        </div>
      </div>

      {submitError && (
        <div className="mb-4 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
          {submitError}
        </div>
      )}

      {/* Mobile-first accordion */}
      <div className="md:hidden space-y-3">
        {questions.map((q, idx) => (
          <details key={q.position} className="bg-white rounded-2xl shadow-sm border border-gray-100 group" open={q.position === active}>
            <summary
              className="cursor-pointer px-4 py-3 list-none flex items-start gap-2"
              onClick={(e) => { e.preventDefault(); setActive(q.position) }}
            >
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mt-0.5">Q{idx + 1}</span>
              <p className="flex-1 text-sm font-bold text-[#1E293B]">{q.text}</p>
              <AnswerStatusDot answered={(drafts[q.position] || '').trim().length > 0} />
            </summary>
            {q.position === active && (
              <div className="px-4 pb-4">
                <AnswerEditor
                  value={drafts[q.position] || ''}
                  onChange={(t) => onTextChange(q.position, t)}
                  onBlur={() => onTextBlur(q.position)}
                  saving={savingMap[q.position]}
                />
              </div>
            )}
          </details>
        ))}
      </div>

      {/* Desktop split-pane */}
      <div className="hidden md:grid grid-cols-[280px_1fr] gap-4">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-2 max-h-[70vh] overflow-y-auto">
          {questions.map((q, idx) => {
            const answered = (drafts[q.position] || '').trim().length > 0
            const isActive = q.position === active
            return (
              <button
                type="button"
                key={q.position}
                onClick={() => setActive(q.position)}
                className={`w-full text-left px-3 py-2.5 rounded-xl flex items-start gap-2 transition ${
                  isActive ? 'bg-[#F47920]/10 ring-1 ring-[#F47920]/30' : 'hover:bg-gray-50'
                }`}
              >
                <span className={`text-[10px] font-bold uppercase tracking-wider mt-0.5 ${isActive ? 'text-[#F47920]' : 'text-gray-400'}`}>
                  Q{idx + 1}
                </span>
                <span className={`flex-1 text-[12.5px] leading-snug line-clamp-2 ${isActive ? 'text-[#1E293B] font-bold' : 'text-gray-700'}`}>
                  {q.text}
                </span>
                <AnswerStatusDot answered={answered} />
              </button>
            )
          })}
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <p className="text-xs font-bold uppercase tracking-wider text-gray-400">
            Question {questions.findIndex((q) => q.position === active) + 1} of {total}
          </p>
          <p className="mt-1 text-base font-bold text-[#1E293B] leading-snug">
            {questions.find((q) => q.position === active)?.text}
          </p>
          <AnswerEditor
            value={drafts[active] || ''}
            onChange={(t) => onTextChange(active, t)}
            onBlur={() => onTextBlur(active)}
            saving={savingMap[active]}
          />
        </div>
      </div>

      {/* Footer */}
      <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
        <button
          type="button"
          onClick={() => setShowDecline(true)}
          className="text-xs text-gray-400 hover:text-gray-700 underline underline-offset-4"
        >
          I'm not interested
        </button>
        <button
          type="button"
          onClick={() => setShowSubmitConfirm(true)}
          disabled={answeredCount < total}
          className="inline-flex items-center gap-2 bg-[#F47920] hover:bg-[#E06810] text-white font-bold py-3 px-6 rounded-2xl shadow-md shadow-orange-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Send className="w-4 h-4" />
          Submit final answers
        </button>
      </div>
      <p className="mt-3 text-center text-xs text-gray-400">
        Your answers are saved automatically. You can leave and come back to finish.
      </p>

      {showSubmitConfirm && (
        <ConfirmModal
          title="Submit your answers?"
          message={`Once you submit, ${data.workspace_name} will see your answers and you won't be able to edit them.`}
          confirmLabel={submitting ? 'Submitting…' : 'Submit'}
          confirmDisabled={submitting}
          onConfirm={finalSubmit}
          onCancel={() => setShowSubmitConfirm(false)}
        />
      )}

      {showDecline && (
        <DeclineModal
          submitting={submitting}
          onConfirm={declineSubmit}
          onCancel={() => setShowDecline(false)}
        />
      )}
    </div>
  )
}

function AnswerEditor({ value, onChange, onBlur, saving }) {
  return (
    <div className="mt-3">
      <textarea
        rows={10}
        className="w-full text-[14px] outline-none bg-gray-50 rounded-2xl px-4 py-3 border border-transparent focus:border-[#F47920] focus:bg-white transition resize-y min-h-[200px]"
        placeholder="Type your answer here…"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
      />
      <div className="mt-1.5 flex items-center justify-between text-[11px] text-gray-400">
        <span>
          {saving === 'saving' ? 'Saving…' : ''}
        </span>
        <span className={value.length >= RESPONSE_LIMIT ? 'text-amber-600 font-semibold' : ''}>
          {value.length} / {RESPONSE_LIMIT}
        </span>
      </div>
    </div>
  )
}

function AnswerStatusDot({ answered }) {
  return (
    <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${answered ? 'bg-emerald-500' : 'bg-gray-200'}`} />
  )
}

function SavedIndicator({ state, at }) {
  if (state === 'saving') {
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-gray-400">
        <Loader2 className="w-3 h-3 animate-spin" />
        Saving…
      </span>
    )
  }
  if (at) {
    const time = at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-600">
        <CheckCircle2 className="w-3 h-3" />
        Saved at {time}
      </span>
    )
  }
  return null
}

function Progress({ answered, total }) {
  const pct = total === 0 ? 0 : Math.round((answered / total) * 100)
  return (
    <div className="flex items-center gap-2">
      <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
        {answered} / {total}
      </div>
      <div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full bg-[#F47920] rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Confirmation modals
// ─────────────────────────────────────────────────────────────────────────────

function ConfirmModal({ title, message, confirmLabel, confirmDisabled, onConfirm, onCancel }) {
  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center px-4"
      style={{ background: 'rgba(15,23,42,0.55)' }}
    >
      <div className="bg-white rounded-3xl shadow-2xl p-6 max-w-md w-full">
        <h2 className="text-lg font-bold text-[#1E293B]">{title}</h2>
        <p className="mt-2 text-sm text-gray-500">{message}</p>
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={confirmDisabled}
            className="px-4 py-2 rounded-xl text-sm font-bold bg-[#F47920] hover:bg-[#E06810] text-white disabled:opacity-50"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

function DeclineModal({ submitting, onConfirm, onCancel }) {
  const [reason, setReason] = useState('')
  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center px-4"
      style={{ background: 'rgba(15,23,42,0.55)' }}
    >
      <div className="bg-white rounded-3xl shadow-2xl p-6 max-w-md w-full">
        <h2 className="text-lg font-bold text-[#1E293B]">Stop the screening?</h2>
        <p className="mt-2 text-sm text-gray-500">
          We'll let the recruiter know you're not interested. Optionally tell them why so they don't reach out again with the same role.
        </p>
        <textarea
          rows={3}
          placeholder="Optional — e.g. not relocating, accepted another offer, salary mismatch."
          className="mt-4 w-full text-[13px] outline-none bg-gray-50 rounded-2xl px-4 py-3 border border-transparent focus:border-[#F47920] focus:bg-white transition resize-y"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={2000}
          disabled={submitting}
        />
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="px-4 py-2 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-100"
          >
            Keep going
          </button>
          <button
            type="button"
            onClick={() => onConfirm(reason)}
            disabled={submitting}
            className="px-4 py-2 rounded-xl text-sm font-bold bg-gray-700 hover:bg-gray-800 text-white"
          >
            {submitting ? 'Sending…' : 'Stop screening'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Done states (terminal)
// ─────────────────────────────────────────────────────────────────────────────

function Done({ variant, data }) {
  if (variant === 'completed') {
    return (
      <div className="text-center max-w-md">
        <div className="w-14 h-14 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto">
          <CheckCircle2 className="w-7 h-7" />
        </div>
        <h1 className="mt-4 text-2xl font-bold text-[#1E293B]">Thanks, {data.candidate_name.split(/\s+/)[0]}!</h1>
        <p className="mt-2 text-sm text-gray-500">
          Your answers have been sent to <strong>{data.workspace_name}</strong>. They'll be in touch by email.
        </p>
      </div>
    )
  }
  if (variant === 'declined') {
    return (
      <div className="text-center max-w-md">
        <div className="w-14 h-14 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center mx-auto">
          <X className="w-7 h-7" />
        </div>
        <h1 className="mt-4 text-2xl font-bold text-[#1E293B]">No problem.</h1>
        <p className="mt-2 text-sm text-gray-500">
          We've let <strong>{data.workspace_name}</strong> know.
        </p>
      </div>
    )
  }
  // expired
  return (
    <div className="text-center max-w-md">
      <div className="w-14 h-14 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mx-auto">
        <Clock className="w-7 h-7" />
      </div>
      <h1 className="mt-4 text-2xl font-bold text-[#1E293B]">This screening expired</h1>
      <p className="mt-2 text-sm text-gray-500">
        Please contact <strong>{data.workspace_name}</strong> if you'd like to continue.
      </p>
    </div>
  )
}
